<script setup lang="ts">
import { computed } from "vue";
import type { Standard } from "../../types";
import { useDtfStore } from "../../store";
import Headline from "../components/Headline.vue";
import Section from "../components/Section.vue";
import PopularBadge from "./PopularBadge.vue";
import TrimInfo from "../../TrimInfo.vue";

defineProps<{
  artwork: Standard.Artwork;
  reorderEnabled: boolean;
  paidExpertEditAddonEnabled?: boolean | undefined;
  index: number;
}>();

const store = useDtfStore();
const isFaceliftExperimentActive = computed(() => store.isFaceliftExperimentActive);
</script>

<template>
  <Headline class="print-perfect__headline">
    <PopularBadge />
    <span
      :class="[
        'print-perfect__free-text',
        { 'print-perfect__free-text--experiment': isFaceliftExperimentActive }
      ]"
    >
      FREE
    </span>
    <template #title>Image Enhanced by Jiffy AI</template>

    <template #subtitle>Background removal, upscaling, sharpening edges.</template>
  </Headline>

  <Section
    v-if="artwork.canRemoveBg"
    class="print-perfect__controls-section print-perfect__addon-container"
  >
    <TrimInfo
      v-if="artwork.canRemoveBg && !artwork.isFile && !artwork.isSimulated"
      :id="artwork.id"
      :artwork="artwork"
      :active-config-section="'jiffy-print-perfect'"
      :can-remove-bg="artwork.canRemoveBg"
      :remove-background="artwork.removeBackground"
      :toggle-background-error="artwork.toggleBackgroundError || ''"
    />
  </Section>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "../ArtworkQualityConfigPanel.scss";

.print-perfect {
  &__headline {
    @include with-quality-container {
      padding-left: 60px;
      padding-right: 130px;
      margin-bottom: 12px; // Reduced from 20px
    }
    margin-bottom: 8px; // Added for mobile/default view
  }

  &__controls-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  &__trim-section {
    margin-bottom: 8px;
  }

  &__list-section {
    flex-direction: column;
    margin-bottom: 8px;
  }

  &__list-section-heading {
    font-weight: 700;
    margin-bottom: 16px;
    text-align: center;
  }

  &__comparison-img {
    width: 100%;

    // Fix for safari
    outline-offset: -1px;
    outline: 1px solid $crafterpay-gray-modern-50;
  }

  &__free-text {
    color: $green-discount-text;
    font-weight: 700;
    font-size: 20px;
    display: flex;
    flex-direction: column;
    line-height: 1;
    flex-shrink: 0;
    gap: 4px;
    position: absolute;
    right: 16px;

    &--experiment {
      background-color: $green-discount-text;
      color: $white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.3;
      white-space: nowrap;
      text-transform: uppercase;
    }
  }
}
</style>
